import * as React from "react";
import Downshift, { DownshiftState, StateChangeOptions } from "downshift";
import styled from "styled-components";

import { MenuLayer, Tag } from "@hubper/core-ui/components";
import { SelectMenuItem, SelectMenu } from "@hubper/core-ui/elements";
import highlightText from "@hubper/core-ui/util/highlightText";
import { PoseGroup } from "react-pose";

import Input from "../components/Input";

const Base = styled.div`
  height: 100%;
  flex: 1 1;
`;

const TagWrapper = styled.div`
  padding: 4px 12px;
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid #e6e6e6;
`;

type Tag = {
  id: string;
  name: string;
};

type Props = {
  style?: React.CSSProperties;
  className?: string;
  items: Tag[];
  placeholder?: string;
  onChange: (value: Tag[]) => void;
  value: Tag[];
};

function Tags(
  { style, className, items, placeholder, onChange, value }: Props,
  ref: any
) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [hasFocus, setFocus] = React.useState(false);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current!.focus();
    }
  }));

  const stateReducer = (
    state: DownshiftState<Tag>,
    changes: StateChangeOptions<Tag>
  ) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          highlightedIndex: state.highlightedIndex,
          isOpen: true,
          inputValue: ""
        };
      default: {
        return changes;
      }
    }
  };

  return (
    <Downshift
      itemToString={x => (x ? x.name : "")}
      onChange={item => {
        onChange((value || []).concat(item));
      }}
      selectedItem={null}
      stateReducer={stateReducer}
    >
      {({
        getRootProps,
        getInputProps,
        isOpen,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        inputValue
      }) => {
        let filteredItems = [] as Tag[];
        if (inputValue) {
          const lc = inputValue.toLowerCase();
          filteredItems = items
            .filter(item => {
              if (value.find(x => x.id === item.id)) {
                return false;
              }

              if (item.name.toLowerCase().includes(lc)) {
                return true;
              }

              return false;
            })
            .sort(
              (a, b) =>
                a.name.toLowerCase().indexOf(lc) -
                b.name.toLowerCase().indexOf(lc)
            )
            .slice(0, 5);
        }

        const showMenu =
          (isOpen && filteredItems.length > 0) ||
          (hasFocus && value.length > 0);

        return (
          <Base
            {...getRootProps({ refKey: "ref" })}
            style={style}
            className={className}
            data-testid="tags-filter"
          >
            <>
              <Input
                isOpen={showMenu}
                placeholder={placeholder}
                isDropDown
                {...getInputProps({
                  ref: inputRef,
                  onFocus: () => setFocus(true),
                  onBlur: () => setFocus(false),
                  value: hasFocus
                    ? inputValue
                    : value.map(x => x.name).join(", ")
                }) as any}
              />
              <MenuLayer element={inputRef.current}>
                {style => (
                  <PoseGroup flipMove={false}>
                    {showMenu && (
                      <SelectMenu
                        key="menu"
                        {...getMenuProps({
                          style
                        })}
                      >
                        {value.length > 0 && (
                          <TagWrapper
                            onMouseDown={evt => {
                              evt.preventDefault();
                              evt.stopPropagation();
                            }}
                          >
                            {value.map(item => (
                              <Tag
                                key={item.id}
                                text={item.name}
                                onDelete={() => {
                                  onChange(value.filter(x => x.id !== item.id));
                                }}
                              />
                            ))}
                          </TagWrapper>
                        )}
                        {filteredItems.map((item, index) => {
                          return (
                            <SelectMenuItem
                              {...getItemProps({
                                item,
                                index
                              })}
                              key={item.id}
                              style={{ padding: "4px 12px" }}
                              highlighted={index === highlightedIndex}
                            >
                              {highlightText(item.name, inputValue || "")}
                            </SelectMenuItem>
                          );
                        })}
                      </SelectMenu>
                    )}
                  </PoseGroup>
                )}
              </MenuLayer>
            </>
          </Base>
        );
      }}
    </Downshift>
  );
}

export default React.forwardRef(Tags);
